import { AppType } from '@features/contexts/AppProvider';
import { ArrivalLabel, LocationTrackingDisplayData, TransformedLocationData } from './types';
import { CaseArrivalTime, DeviceNodeType } from '@/api/apis/composer';
import { convertToUserLocalTime, formatDateTimeStringToTimeZone, getUserTimezone } from '@/utils/helpers';

export type DisplayDataCaseFields = {
  appType: AppType;
  donorHospitalDate?: string | null;
  donorHospitalTime?: string | null;
  donorHospitalTimeZone?: string | null;
  txEtaHospitalDate?: string | null;
  txEtaHospitalTimeZone?: string | null;
  txEtaHospitalTime?: string | null;
  organOnOCSTimestamp?: string | null;
  caseName?: string | null;
  donorHospitalCode?: string | null;
  txCenterCode?: string | null;
  recipientHospitalDate?: string | null;
  recipientHospitalTime?: string | null;
  recipientHospitalTimeZone?: string | null;
};

export const formatTimeRemaining = (eta: string): string | null => {
  const etaDate = new Date(eta);
  const now = new Date();
  const diffMs = etaDate.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  const totalMinutes = Math.ceil(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export type OrganArrivalDisplay = {
  label: ArrivalLabel;
  dateTimeString: string | null;
};

/**
 * 3-tier priority: Organ ETA (live) > STA from TMDX Drive > fallback case fields.
 * When displayEta is false, the live ETA tier is skipped.
 * All timestamps are converted to the user's local timezone.
 */
export const resolveOrganArrivalDisplay = (
  displayEta: boolean,
  arrivalData: CaseArrivalTime | undefined,
  fallbackDate?: string | null,
  fallbackTime?: string | null,
  fallbackTz?: string | null,
): OrganArrivalDisplay => {
  const eta = displayEta ? arrivalData?.arrivalTime?.eta : null;
  const sta = arrivalData?.arrivalTime?.sta;
  const userTz = getUserTimezone();

  if (eta) {
    return { label: 'Organ ETA', dateTimeString: formatDateTimeStringToTimeZone(eta, userTz) };
  }
  if (sta) {
    return { label: 'Organ STA', dateTimeString: formatDateTimeStringToTimeZone(sta, userTz) };
  }
  return {
    label: 'Organ STA',
    dateTimeString: convertToUserLocalTime(fallbackDate, fallbackTime, fallbackTz),
  };
};

const splitTimeTz = (formatted: string | null): { date: string; time: string; tz: string } | null => {
  if (!formatted) return null;
  const parts = formatted.split(' ');
  if (parts.length < 3) return null;
  const [rawDate, time, tz] = parts.slice(-3);
  const date = rawDate.split('/').slice(0, 2).join('/');
  return { date, time, tz };
};

export const buildLocationTrackingDisplayData = (
  caseFields: DisplayDataCaseFields,
  arrivalData: CaseArrivalTime | undefined,
): LocationTrackingDisplayData => {
  const isConnectApp = caseFields.appType === AppType.NopConnectPlatform;
  const donorOrParts = splitTimeTz(
    convertToUserLocalTime(
      caseFields.donorHospitalDate,
      caseFields.donorHospitalTime,
      caseFields.donorHospitalTimeZone,
    ),
  );

  const isEtaVisible = arrivalData?.displaySettings?.displayEstimatedTimeOfArrival ?? false;
  const displayEta = isConnectApp || isEtaVisible;
  const etaTimestamp = displayEta ? arrivalData?.arrivalTime?.eta || null : null;

  const { label: arrivalLabel, dateTimeString: etaFormatted } = resolveOrganArrivalDisplay(
    displayEta,
    arrivalData,
    caseFields.txEtaHospitalDate,
    caseFields.txEtaHospitalTime,
    caseFields.txEtaHospitalTimeZone,
  );
  const etaParts = splitTimeTz(etaFormatted);

  const txParts = splitTimeTz(
    convertToUserLocalTime(
      caseFields.recipientHospitalDate,
      caseFields.recipientHospitalTime,
      caseFields.recipientHospitalTimeZone,
    ),
  );

  return {
    donorOrTime: donorOrParts?.time ?? null,
    donorOrTz: donorOrParts?.tz ?? null,
    etaDate: etaParts?.date ?? null,
    etaTime: etaParts?.time ?? null,
    etaTz: etaParts?.tz ?? null,
    arrivalLabel,
    organOnOCSTimestamp: caseFields.organOnOCSTimestamp ?? null,
    caseName: caseFields.caseName ?? '--',
    donorHospitalCode: caseFields.donorHospitalCode ?? '--',
    txCenterCode: caseFields.txCenterCode ?? '--',
    txCenterOrTime: txParts?.time ?? null,
    txCenterOrTz: txParts?.tz ?? null,
    etaTimestamp,
  };
};

export const checkDeviceGroundNode = (transformedData: TransformedLocationData | null) => {
  const activeNodeType = transformedData?.activePosition?.nodeType;
  if (activeNodeType) return activeNodeType === DeviceNodeType.Ground;
  const lastCompletedNodeType = transformedData?.devicePolylines.filter((p) => p.isComplete).at(-1)?.nodeType;
  if (lastCompletedNodeType) return lastCompletedNodeType === DeviceNodeType.Ground;
  const hasAviaNodeTypes = transformedData?.devicePolylines.some((p) => p.nodeType === DeviceNodeType.Air);

  return !hasAviaNodeTypes;
};
